// A small Markdown editor: a textarea with a Write / Preview toggle, plus rich paste (pasting formatted
// content from Word / Google Docs / the web drops in as Markdown). Stores plain Markdown text. With
// `maxLength` it counts the characters under the box, in red once there are too many to save (a paste goes in
// at the caret, so text already in the box counts too).
import { useRef, useState, type ClipboardEvent } from 'react';
import { htmlToMarkdown, renderMarkdown } from '../lib/markdown.js';
import { Segmented } from './ui.js';

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minHeight = 160,
  ariaLabel,
  maxLength,
}: {
  value: string;
  onChange: (md: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** accessible name for the textarea (the placeholder is only a hint). */
  ariaLabel?: string;
  /** how long the text may be: shows a count under the box */
  maxLength?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [tab, setTab] = useState<'write' | 'preview'>('write');

  // paste rich content as Markdown; splice it in at the caret / over the selection.
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (!html || !html.trim()) return; // plain text pastes normally
    const md = htmlToMarkdown(html);
    if (!md) return;
    e.preventDefault();
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + md + value.slice(end);
    onChange(next);
    // restore the caret just after the inserted text on the next tick.
    requestAnimationFrame(() => { if (el) { const pos = start + md.length; el.focus(); el.setSelectionRange(pos, pos); } });
  };

  return (
    <div className="md-editor">
      <div className="md-tabs">
        <Segmented
          value={tab}
          onChange={setTab}
          ariaLabel="Editor mode"
          options={[{ value: 'write', label: 'Write' }, { value: 'preview', label: 'Preview' }]}
        />
        <span className="md-hint muted">Markdown supported. Paste keeps formatting.</span>
      </div>
      {tab === 'write' ? (
        <textarea
          ref={ref}
          className="field md-area"
          style={{ width: '100%', minHeight, resize: 'vertical', fontFamily: 'var(--font-mono, monospace)' }}
          value={value}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
        />
      ) : (
        <div className="md-body md-preview" style={{ minHeight }} dangerouslySetInnerHTML={{ __html: renderMarkdown(value) || '<p class="muted">Nothing to preview.</p>' }} />
      )}
      {maxLength != null && (
        <div className={`md-count${value.length > maxLength ? ' is-over' : ''}`} aria-live="polite">
          {value.length.toLocaleString('en-GB')} / {maxLength.toLocaleString('en-GB')} characters
          {value.length > maxLength && ` · ${(value.length - maxLength).toLocaleString('en-GB')} too many to save`}
        </div>
      )}
    </div>
  );
}
